import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { T3ApiResponse } from './t3-response-model';

export type T3UpdateAction = 'rename' | 'delete';

export type T3SimulationParameters = Record<string, any>;

export interface CreateT3SimulationRequest {
  session_id: string;
  id: string;
  city_name: string;
  simulation_run_name: string;
  intervention_names: string;
  parameter_details: T3SimulationParameters;
}

export interface RenameT3SimulationRequest {
  model: 't3';
  action: 'rename';
  session_id: string;
  city: string;
  id: string;
  old_simulation_run_name: string;
  new_simulation_run_name: string;
}

export interface DeleteT3SimulationRequest {
  model: 't3';
  action: 'delete';
  session_id: string;
  city: string;
  id: string;
  old_simulation_run_name: string;
}

export interface RenameT3Args {
  session_id: string;
  city: string;
  id: string;
  old_simulation_run_name: string;
  new_simulation_run_name: string;
}

export interface DeleteT3Args {
  session_id: string;
  city: string;
  id: string;
  old_simulation_run_name: string;
}

export interface ApiStatusResponse {
  status: 'success' | 'fail' | string;
  message?: string;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class T3SimulationService {
  private readonly headers = new HttpHeaders({
    Authorization: `Bearer ${environment.handshakeToken}`,
    'Content-Type': 'application/json'
  });

  private readonly createUrl = `${environment.apiUrl}/t3_simulation`;
  private readonly updateUrl = `${environment.apiUrl}/update`;

  constructor(private http: HttpClient) {}

  /**
   * /t3_simulation returns BASE64 JSON -> read as text and decode.
   */
  async createSimulation(payload: CreateT3SimulationRequest): Promise<T3ApiResponse> {
    try {
      const text = await firstValueFrom(
        this.http.post(this.createUrl, payload, {
          headers: this.headers,
          responseType: 'text'
        })
      );

      return this.decodeB64Json<T3ApiResponse>(text);
    } catch (e) {
      throw this.decodeB64HttpError(e);
    }
  }

  /**
   * /update is plain JSON.
   */
  renameSimulation(args: RenameT3Args): Promise<ApiStatusResponse> {
    const body: RenameT3SimulationRequest = {
      model: 't3',
      action: 'rename',
      session_id: args.session_id,
      city: args.city,
      id: args.id,
      old_simulation_run_name: args.old_simulation_run_name,
      new_simulation_run_name: args.new_simulation_run_name
    };

    return firstValueFrom(
      this.http.post<ApiStatusResponse>(this.updateUrl, body, {
        headers: this.headers
      })
    );
  }

  /**
   * /update is plain JSON.
   */
  deleteSimulation(args: DeleteT3Args): Promise<ApiStatusResponse> {
    const body: DeleteT3SimulationRequest = {
      model: 't3',
      action: 'delete',
      session_id: args.session_id,
      city: args.city,
      id: args.id,
      old_simulation_run_name: args.old_simulation_run_name
    };

    return firstValueFrom(
      this.http.post<ApiStatusResponse>(this.updateUrl, body, {
        headers: this.headers
      })
    );
  }

  private decodeB64Json<T>(value: string): T {
    const raw = String(value || '').trim();

    try {
      const json = atob(raw);
      return JSON.parse(json) as T;
    } catch {
      return JSON.parse(raw) as T;
    }
  }

  private decodeB64HttpError(err: any): Error {
    const e = err as HttpErrorResponse;

    try {
      if (typeof e?.error === 'string') {
        const decoded = this.decodeB64Json<any>(e.error);
        const msg = decoded?.message || decoded?.error || 'Request failed.';
        return new Error(msg);
      }
    } catch {}

    const msg = e?.message || 'Request failed.';
    return new Error(msg);
  }
}